"use client";
import React, { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "@/hooks/localization/useTranslations";
import AppToast from "@/app/components/lib/toast/AppToast";
import CommonFormBuilder from "@/app/components/common-form-builder/CommonFormBuilder";
import {
  FieldTypes,
  IField,
} from "@/api/AppApi/services/builder/interfaces/interfaces";
import {
  createCustomFields,
  formErrors,
} from "@/app/components/common-form-builder/form-builder-helpers/form-functions";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "@/app/components/lib/CustomButton";
import { useRemindMe } from "@/api/AppApi/services/remind-stock";
import PopUp from "@/app/components/common-builder/pop-ups-components/pop-up-helpers/PopUp";
import { useLang } from "@/providers/langProvider";
import SvgExit from "@/app/components/common-builder/pop-ups-components/pop-up-helpers/SvgExit";
import ProductStars from "@/app/components/review-product/product-details-rate/ProductStars";
import { ReviewVisibility } from "@/api/AppApi/services/company/interfaces/ICompany";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { Spinner } from "react-bootstrap";

type Product = {
  name: string;
  description: string;
  image: string;
  rate: any;
  id: number;
};

interface Props {
  product: Product;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
}

const ReminderForm: React.FC<Props> = ({ product, isOpen, setIsOpen }) => {
  const t = useTranslation();
  const [active, setActive] = useState<0 | 1>(0); // 0: email, 1: whatsapp
  const [message, setMessage] = useState<string | boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const lang = useLang();
  const company = useGetCompanyQuery();
  const { theme } = company?.data?.result ?? {};
  const showRate =
    theme?.components?.product?.product_reviews_visibility !==
    ReviewVisibility.none;
  const fields: IField[] = useMemo(
    () =>
      createCustomFields([
        {
          key: "email",
          type: FieldTypes.email,
          title: t["e-mail"],
          is_required: active === 0,
          value: "",
        },
        {
          key: "mobile",
          type: FieldTypes.mobile,
          title: t.phone,
          is_required: active === 1,
          value: { number: "", dial: null },
        },
      ]),
    [t, active],
  );

  const form = useForm({
    defaultValues: {
      email: "",
      mobile: { number: "", dial: null as any },
      language: lang,
    },
    mode: "onChange",
  });

  const remind = useRemindMe({
    onSuccess: () => {
      setError(null);
      setMessage(t.request_success ?? "Success");
      form.reset();
      setTimeout(() => {
        setMessage(false);
        setIsOpen(false);
      }, 1000);
    },
    onError: (err) => {
      setError(err?.response?.data?.message?.text ?? "");
      formErrors(err, form as any);
      setTimeout(() => {
        setMessage(false);
      }, 5000);
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    if (active === 0) {
      remind.mutate({
        type: "email",
        email: data.email,
        mobile: undefined,
        product: product.id,
        language: lang,
      });
    } else {
      const mobile = {
        number: (data as any)?.mobile?.number,
        country: (data as any)?.mobile.dial?.iso2?.toUpperCase(),
      };
      remind.mutate({
        type: "whatsapp",
        email: "",
        mobile,
        product: product.id,
        language: lang,
      });
    }
  });

  return (
    <>
      <PopUp
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        contentClassName="reminder-popup"
      >
        <AppToast
          danger={!!error}
          success={!error && !!message}
          message={message}
          setShow={setMessage}
          show={!!message}
        />
        <div className="reminder-popup-header">
          <p>{t.stock_reminder}</p>
          <SvgExit style={{ fill: "black" }} onClick={() => setIsOpen(false)} />
        </div>

        <div className="p-4">
          <div className="container">
            <div className="row align-items-center g-3">
              <div
                style={{
                  objectFit: "contain",
                  width: "103px",
                  height: "87px",
                }}
                className="col-md-4 text-center p-0 m-0"
              >
                <img
                  src={product.image}
                  alt="Product Image"
                  className="img-fluid rounded w-100 h-100"
                  style={{
                    objectFit: "cover",
                  }}
                />
              </div>

              <div className="col-md-8">
                <h5 className="reminder-popup-product-title">
                  {product.name}{" "}
                </h5>
                <p className="reminder-popup-product-description">
                  {product.description}{" "}
                </p>
                {showRate && <ProductStars rate={product.rate} />}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-center">
          <div className="auth-tabs-container">
            <button
              className={`tab ${
                active === 0 ? "tab-selected" : "tab-not-selected"
              }`}
              onClick={() => setActive(0)}
              type="button"
            >
              {t["e-mail"]}
            </button>
            <button
              className={`tab ${
                active === 1 ? "tab-selected" : "tab-not-selected"
              }`}
              onClick={() => setActive(1)}
              type="button"
            >
              Whatsapp
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-4 mb-4">
          {active === 0 && (
            <CommonFormBuilder field={fields[0]} form={form as any} />
          )}
          {active === 1 && (
            <CommonFormBuilder field={fields[1]} form={form as any} />
          )}

          <div className="d-flex justify-content-end mt-3">
            <CustomButton
              size={BtnSizes.small}
              type={BtnTypes.primary}
              radius={BtnRadius.small}
              isSubmit
              disabled={remind.isLoading}
            >
              {remind.isLoading ? <Spinner size="sm" /> : t.submit}
            </CustomButton>
          </div>
        </form>
      </PopUp>
    </>
  );
};

export default ReminderForm;
